import { useRef, useState, type ChangeEvent } from 'react';
import { IonButton, IonIcon, IonSpinner } from '@ionic/react';
import {
  cameraOutline,
  closeCircleOutline,
  documentAttachOutline,
  folderOpenOutline,
  imagesOutline,
} from 'ionicons/icons';
import { getCaptureProvider } from '../lib/adapters/camera/index.js';
import { prepareDocumentFile } from '../lib/documents/documentSource.js';
import { MAX_PAGES } from '../lib/documents/captureTray.js';
import { uploadPageToStaging } from '../lib/documents/documentJobsClient.js';
import {
  attachDocumentImportReviewOriginal,
  deferDocumentImportReviewOriginal,
  repairDocumentImportReviewOriginal,
} from '../lib/documents/documentImportReviews.js';
import { getSupabase } from '../lib/supabase.js';
import { confirmDialog } from '../lib/ui/dialogs.js';
import './ReviewOriginalRecoveryPanel.css';

interface RecoveryPage {
  id: string;
  b64: string;
  mime: string;
  name: string;
}

const MAX_RECOVERY_FILE_BYTES = 50_000_000;

function base64Bytes(b64: string): number {
  return Math.floor((b64.length * 3) / 4);
}

function pageCountReady(pages: RecoveryPage[], expectedPages: number | null): boolean {
  if (pages.length === 1 && pages[0].mime === 'application/pdf') return true;
  return pages.length > 0 && (expectedPages == null || pages.length === expectedPages);
}

export function ReviewOriginalRecoveryPanel(props: {
  tenantId: string;
  reviewId: string;
  expectedPages: number | null;
  mode?: 'recover' | 'attach';
  onRecovered: () => void;
  onDeferred?: () => void;
}): JSX.Element {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pages, setPages] = useState<RecoveryPage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attachingLater = props.mode === 'attach';
  const expectedLabel = props.expectedPages == null
    ? 'tutte le pagine del documento'
    : `${props.expectedPages} pagine`;

  async function imagePage(b64: string, mime: string, name: string): Promise<RecoveryPage> {
    const source = await prepareDocumentFile(b64, mime);
    return { id: crypto.randomUUID(), b64: source.b64, mime: source.mime, name };
  }

  function addPages(next: RecoveryPage[]): void {
    setError(null);
    setPages((current) => [...current, ...next].slice(0, MAX_PAGES));
  }

  async function addFromCamera(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const photo = await getCaptureProvider().takePhoto('camera', { quality: 100 });
      if (photo) addPages([await imagePage(photo.base64, photo.mime, `Pagina ${pages.length + 1}`)]);
    } catch {
      setError('Non è stato possibile acquisire la foto. Riprova o scegli il file dalla galleria.');
    } finally {
      setBusy(false);
    }
  }

  async function addFromGallery(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const remaining = Math.max(0, MAX_PAGES - pages.length);
      const photos = await getCaptureProvider().pickPhotos('gallery', remaining, { quality: 100 });
      const next: RecoveryPage[] = [];
      for (const [index, photo] of photos.entries()) {
        next.push(await imagePage(photo.base64, photo.mime, `Pagina ${pages.length + index + 1}`));
      }
      addPages(next);
    } catch {
      setError('Non è stato possibile leggere le immagini selezionate.');
    } finally {
      setBusy(false);
    }
  }

  async function handleFiles(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      if (files.some((file) => file.type === 'application/pdf') && files.length !== 1) {
        setError('Se scegli un PDF, seleziona un solo file completo. In alternativa scegli tutte le immagini delle pagine.');
        return;
      }
      const next: RecoveryPage[] = [];
      for (const [index, file] of files.entries()) {
        if (file.size > MAX_RECOVERY_FILE_BYTES) {
          throw new Error('file_too_large');
        }
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result ?? ''));
          reader.onerror = () => reject(new Error('file_read_failed'));
          reader.readAsDataURL(file);
        });
        const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
        if (file.type === 'application/pdf') {
          next.push({ id: crypto.randomUUID(), b64, mime: file.type, name: file.name });
        } else {
          next.push(await imagePage(b64, file.type || 'image/jpeg', file.name || `Pagina ${pages.length + index + 1}`));
        }
      }
      setPages(next.slice(0, MAX_PAGES));
    } catch (caught) {
      setError(caught instanceof Error && caught.message === 'file_too_large'
        ? 'Il file supera 50 MB. Riducilo oppure acquisisci le pagine come foto.'
        : 'Non è stato possibile leggere il file selezionato.');
    } finally {
      setBusy(false);
    }
  }

  async function recover(): Promise<void> {
    if (!pageCountReady(pages, props.expectedPages)) return;
    setBusy(true);
    setError(null);
    const uploadedPaths: string[] = [];
    try {
      for (const page of pages) {
        if (base64Bytes(page.b64) > MAX_RECOVERY_FILE_BYTES) {
          throw new Error('file_too_large');
        }
        uploadedPaths.push(await uploadPageToStaging(props.tenantId, page.b64, page.mime));
      }
      if (attachingLater) {
        await attachDocumentImportReviewOriginal({
          tenantId: props.tenantId,
          reviewId: props.reviewId,
          replacementPagePaths: uploadedPaths,
        });
      } else {
        await repairDocumentImportReviewOriginal({
          tenantId: props.tenantId,
          reviewId: props.reviewId,
          replacementPagePaths: uploadedPaths,
        });
      }
      setPages([]);
      props.onRecovered();
    } catch (caught) {
      if (uploadedPaths.length > 0) {
        await getSupabase().storage.from('fiscal-photo-staging').remove(uploadedPaths);
      }
      const message = caught instanceof Error ? caught.message : '';
      if (message === 'file_too_large') {
        setError('Una pagina supera 50 MB. Riduci il file e riprova.');
      } else if (message.startsWith('review_source_page_count_mismatch:')) {
        const [, expected, received] = message.split(':');
        setError(`Il documento caricato contiene ${received} pagine, ma l’originale ne aveva ${expected}. Ricarica il documento completo.`);
      } else {
        setError(attachingLater
          ? 'Il salvataggio dell’originale non è riuscito. Il carico resta valido: controlla la connessione e riprova.'
          : 'Il recupero dell’originale non è riuscito. Le modifiche alla revisione restano salvate: controlla la connessione e riprova.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function deferOriginal(): Promise<void> {
    if (!props.onDeferred) return;
    const confirmed = await confirmDialog(
      'Il magazzino verrà caricato senza il documento originale. Il carico resterà tracciato come “originale da integrare” e potrai aggiungerlo in seguito dalla scheda documento.',
      {
        header: 'Caricare senza originale?',
        okText: 'Continua con il carico',
        cancelText: 'Torna al documento',
      },
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await deferDocumentImportReviewOriginal({
        tenantId: props.tenantId,
        reviewId: props.reviewId,
      });
      props.onDeferred();
    } catch {
      setError('Non è stato possibile registrare il rinvio dell’originale. Riprova senza chiudere la revisione.');
    } finally {
      setBusy(false);
    }
  }

  const ready = pageCountReady(pages, attachingLater ? null : props.expectedPages);
  const countMessage = pages.length === 1 && pages[0].mime === 'application/pdf'
    ? (attachingLater
      ? 'PDF selezionato.'
      : 'PDF completo selezionato. Il numero di pagine sarà verificato prima del salvataggio.')
    : (attachingLater
      ? `${pages.length} ${pages.length === 1 ? 'pagina selezionata' : 'pagine selezionate'}.`
      : `${pages.length} di ${props.expectedPages ?? 'tutte le'} pagine selezionate.`);

  return (
    <section className="te-review-source-recovery" role="alert" aria-labelledby="review-source-recovery-title">
      <div className="te-review-source-recovery__heading">
        <IonIcon icon={documentAttachOutline} aria-hidden="true" />
        <div>
          <h3 id="review-source-recovery-title">
            {attachingLater ? 'Originale da integrare' : 'Originale da recuperare'}
          </h3>
          {attachingLater ? (
            <p>
              Il magazzino è già stato caricato. Aggiungi ora il file originale: articoli, quantità e movimenti non verranno modificati.
            </p>
          ) : (
            <p>
              Questa revisione è precedente al salvataggio permanente dei documenti. Ricarica {expectedLabel}:
              dati, abbinamenti e correzioni già inseriti non verranno modificati.
            </p>
          )}
        </div>
      </div>

      <div className="te-review-source-recovery__actions">
        <IonButton fill="outline" disabled={busy || pages.length >= MAX_PAGES} onClick={() => void addFromCamera()}>
          <IonIcon slot="start" icon={cameraOutline} />
          Scatta
        </IonButton>
        <IonButton fill="outline" disabled={busy || pages.length >= MAX_PAGES} onClick={() => void addFromGallery()}>
          <IonIcon slot="start" icon={imagesOutline} />
          Galleria
        </IonButton>
        <IonButton fill="outline" disabled={busy} onClick={() => fileInputRef.current?.click()}>
          <IonIcon slot="start" icon={folderOpenOutline} />
          File o PDF
        </IonButton>
      </div>

      {pages.length > 0 && (
        <div className="te-review-source-recovery__selection">
          <p>{countMessage}</p>
          <div className="te-review-source-recovery__files">
            {pages.map((page, index) => (
              <div key={page.id} className="te-review-source-recovery__file">
                <span>{page.mime === 'application/pdf' ? 'PDF' : `Pagina ${index + 1}`}</span>
                <button
                  type="button"
                  aria-label={`Rimuovi ${page.name}`}
                  disabled={busy}
                  onClick={() => setPages((current) => current.filter((item) => item.id !== page.id))}
                >
                  <IonIcon icon={closeCircleOutline} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="te-review-source-recovery__error" aria-live="assertive">{error}</p>}

      <IonButton expand="block" disabled={busy || !ready} onClick={() => void recover()}>
        {busy ? <IonSpinner name="dots" /> : attachingLater ? 'Salva originale' : 'Salva originale e continua'}
      </IonButton>
      {!attachingLater && !ready && pages.length > 0 && props.expectedPages != null && (
        <p className="te-review-source-recovery__hint">Seleziona tutte le {props.expectedPages} pagine oppure un unico PDF completo.</p>
      )}

      {!attachingLater && props.onDeferred && (
        <div className="te-review-source-recovery__defer">
          <p>Se l’originale non è più disponibile, puoi completare il carico e integrarlo in seguito.</p>
          <IonButton
            expand="block"
            fill="outline"
            color="warning"
            disabled={busy}
            onClick={() => void deferOriginal()}
          >
            Carica magazzino senza originale
          </IonButton>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        hidden
        onChange={(event) => void handleFiles(event)}
      />
    </section>
  );
}
